/**
 * Build initial Top/Bottom/Pool lists from a question object.
 * @param {Object} questionData
 * @param {Array<{id:number, text:string}>} [questionData.answers]
 * @param {Array<number>} [questionData.topNOptions]
 * @param {Array<number>} [questionData.bottomNOptions]
 * @returns {{ top: Array<{id:string,label:string}>, bottom: Array<{id:string,label:string}>, pool: Array<{id:string,label:string}> }}
 */
export const buildInitialLists = (questionData) => {
  const mapAnswerToItem = (answer) => ({
    id: String(answer.id),
    label: answer.text ?? '',
  });

  const answers = Array.isArray(questionData?.answers) ? questionData.answers : [];
  const topOptionIds = questionData?.topNOptions ?? [];
  const bottomOptionIds = questionData?.bottomNOptions ?? [];

  const idToItem = new Map(answers.map((answer) => [String(answer.id), mapAnswerToItem(answer)]));

  const claimedIds = new Set();

  const toItemsByIds = (ids) => {
    const items = [];
    for (const numericId of ids) {
      const id = String(numericId);
      const item = idToItem.get(id);
      if (!item) continue;
      if (claimedIds.has(id)) continue;
      claimedIds.add(id);
      items.push(item);
    }
    return items;
  };

  const top = toItemsByIds(topOptionIds);
  const bottom = toItemsByIds(bottomOptionIds);
  const pool = answers.map(mapAnswerToItem).filter((item) => !claimedIds.has(item.id));

  return { bottom, pool, top };
};

/**
 * Move an item from one droppable list to another (or within the same if ids differ).
 * Returns updated shallow copies of both source and destination lists keyed by their droppable ids.
 * @template T
 * @param {T[]} source - Source list.
 * @param {T[]} destination - Destination list.
 * @param {{ droppableId: string, index: number }} droppableSource - Source metadata (id and index).
 * @param {{ droppableId: string, index: number }} droppableDestination - Destination metadata (id and index).
 * @returns {Record<string, T[]>} An object mapping droppable ids to their updated item arrays.
 */
export const move = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone = source.slice();
  const destClone = destination.slice();
  const [moved] = sourceClone.splice(droppableSource.index, 1);
  destClone.splice(droppableDestination.index, 0, moved);

  return {
    [droppableDestination.droppableId]: destClone,
    [droppableSource.droppableId]: sourceClone,
  };
};

/**
 * Reorder a list by moving one item from its original index to a new index.
 * Does not mutate the original list.
 * @template T
 * @param {T[]} list - Original array.
 * @param {number} startIndex - Index of the item to move.
 * @param {number} endIndex - Target index for the item.
 * @returns {T[]} A new array with the item repositioned.
 */
export const reorder = (list, startIndex, endIndex) => {
  const result = list.slice();
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};
